<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { mdiCalendarClock } from "@mdi/js"
  import CreateAppointmentOverlay from "../../../../../../../../_shared/_modules/create-appointment-overlay"
  import { buildFixedWorkOrderAppointmentInit } from "../../../../../../../../_shared/_logic/init"
  import ScheduleEventReadOverlay from "../../../../../../../../_shared/_modules/schedule-event-read-overlay"
  import CommunicationPlanTable from "../../../../../../../../_shared/_modules/communication-plan-table"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { WorkingHoursByUser } from "../../../../../../../../../../../../_shared/_logic/form-state"
  import type { ContactOrder } from "../../../../../../../../../../../../api/kehrblatt/contact-orders"
  import type {
    ScheduleEventCommunicationPresentation,
    WorkOrderPlanningContactOrderSummary,
  } from "../../../../../../../../../../../../api/kehrblatt/work-orders"
  import {
    ANNOUNCEMENT_PLAN_EMPTY_TEXT,
    CANCELED_ANNOUNCEMENT_PLAN_EMPTY_TEXT,
    announcementEventIsCanceled,
    buildAppointmentAnnouncementSections,
    type AppointmentAnnouncementOrder,
    type AppointmentAnnouncementSection,
  } from "./_logic/announcementSections"

  type EmployeeOption = { id: string; label: string }

  type Props = {
    organizationId: string
    workOrder: WorkOrder | null
    properties?: Property[]
    scheduleEvents?: ScheduleEvent[]
    allScheduleEvents?: ScheduleEvent[]
    scheduleEventCommunicationPresentations?: Record<string, ScheduleEventCommunicationPresentation>
    relevantScheduleEvent?: ScheduleEvent | null
    relevantScheduleEventId?: string | null
    customers?: Customer[]
    employees?: EmployeeOption[]
    contactOrders?: WorkOrderPlanningContactOrderSummary[]
    contactPoints?: ContactPoint[]
    workingHoursByUser?: WorkingHoursByUser
    allEmployeeIds?: string[]
    onReloadScheduleEvents?: () => void
    onScheduleEventUpdated?: (
      event?: ScheduleEvent | null,
      removedId?: string | null,
    ) => Promise<void> | void
    onPlanningContextInvalidated?: () => Promise<void> | void
    canEdit?: boolean
  }

  let {
    organizationId,
    workOrder = null,
    properties = [],
    scheduleEvents = [],
    allScheduleEvents = [],
    scheduleEventCommunicationPresentations = {},
    relevantScheduleEvent = null,
    relevantScheduleEventId = null,
    customers = [],
    employees = [],
    contactOrders = [],
    contactPoints = [],
    workingHoursByUser = {},
    allEmployeeIds = [],
    onReloadScheduleEvents = () => {},
    onScheduleEventUpdated = async () => {},
    onPlanningContextInvalidated = async () => {},
    canEdit = true,
  }: Props = $props()

  const orderLocked = $derived(Boolean(!canEdit || (workOrder && workOrder.status !== "OPEN")))
  const caseId = $derived(workOrder?.case_id ?? null)

  const contactOrderReasons = new Set(["INITIAL", "RESCHEDULE", "MANUAL"])
  const contactOrderStatuses = new Set(["PLANNED", "ACTIVE", "CLOSED"])
  const contactOrderClosedReasons = new Set([
    "APPOINTMENT_APPROVED",
    "EXHAUSTED",
    "CANCELED",
    "SUPERSEDED",
  ])
  const isContactOrderReason = (value: string | null): value is ContactOrder["reason"] =>
    Boolean(value && contactOrderReasons.has(value))
  const isContactOrderStatus = (value: string | null): value is ContactOrder["status"] =>
    Boolean(value && contactOrderStatuses.has(value))
  const isContactOrderClosedReason = (
    value: string | null,
  ): value is NonNullable<ContactOrder["closed_reason"]> =>
    Boolean(value && contactOrderClosedReasons.has(value))

  const overlayContactOrders = $derived.by(() =>
    contactOrders.flatMap((order): ContactOrder[] => {
      if (!isContactOrderReason(order.reason) || !isContactOrderStatus(order.status)) return []

      return [
        {
          ...order,
          reason: order.reason,
          status: order.status,
          closed_reason: isContactOrderClosedReason(order.closed_reason)
            ? order.closed_reason
            : null,
        },
      ]
    }),
  )

  const scheduleEventById = $derived.by(() => {
    const result: Record<string, ScheduleEvent> = {}
    ;[...(allScheduleEvents ?? []), ...(scheduleEvents ?? [])].forEach((event) => {
      if (event.id) result[event.id] = event
    })

    return result
  })
  const announcementSections = $derived.by(() =>
    buildAppointmentAnnouncementSections({
      workOrder,
      scheduleEvents,
      allScheduleEvents,
      contactOrders,
      relevantScheduleEvent,
      relevantScheduleEventId,
      employees,
      communicationPresentationsByScheduleEventId: scheduleEventCommunicationPresentations,
    }),
  )

  const handleScheduleChange = async (event?: ScheduleEvent | null, removedId?: string | null) => {
    await onScheduleEventUpdated(event, removedId)
  }

  const openEventDetails = (event: ScheduleEvent) => {
    const eventIsPast = event.start_at ? new Date(event.start_at).getTime() < Date.now() : false
    const forceReadOnly =
      orderLocked ||
      eventIsPast ||
      (event.id !== relevantScheduleEventId && !["CONFIRMED", "COMPLETED"].includes(event.status))
    openOverlay({
      title: "Termin",
      layout: "record",
      content: ScheduleEventReadOverlay,
      closeOnParentOpen: true,
      isDark: true,
      properties: {
        organizationId,
        employees,
        workOrders: workOrder ? [workOrder] : [],
        properties,
        customers,
        contactPoints,
        contactOrders: overlayContactOrders,
        scheduleEvents,
        workingHoursByUser,
        event,
        forceReadOnly,
        planningMode: true,
        onSaved: async (updated: ScheduleEvent) => {
          await handleScheduleChange(updated)
          await onReloadScheduleEvents()
        },
        onDeleted: async (id: string) => {
          await handleScheduleChange(null, id)
          await onReloadScheduleEvents()
        },
      },
    })
  }

  const linkedEventFor = (
    order: AppointmentAnnouncementOrder,
    section?: AppointmentAnnouncementSection | null,
  ) =>
    order.schedule_event_id ? scheduleEventById[order.schedule_event_id] : (section?.event ?? null)

  const announcementSectionIsInactive = (section?: AppointmentAnnouncementSection | null) =>
    announcementEventIsCanceled(section?.event)

  const announcementPlanIsInactive = (
    order: AppointmentAnnouncementOrder,
    section?: AppointmentAnnouncementSection | null,
  ) =>
    announcementEventIsCanceled(linkedEventFor(order, section)) ||
    announcementSectionIsInactive(section)

  const inactiveContactOrderStatuses = new Set([
    "CLOSED",
    "COMPLETED",
    "DONE",
    "CANCELED",
    "CANCELLED",
  ])

  const contactOrderIsActive = (order?: AppointmentAnnouncementOrder | null) => {
    const status = String(order?.status || "").toUpperCase()

    return Boolean(order && !inactiveContactOrderStatuses.has(status))
  }

  const emptyTextForSection = (section?: AppointmentAnnouncementSection | null) =>
    section?.emptyText ?? ANNOUNCEMENT_PLAN_EMPTY_TEXT

  const hasOpenContactOrder = (section: AppointmentAnnouncementSection) =>
    !announcementSectionIsInactive(section) && section.orders.some(contactOrderIsActive)

  const reloadAfterContactOrderCreated = async () => {
    await onReloadScheduleEvents()
  }

  const openCreateAppointment = () => {
    if (!workOrder?.id) return

    openOverlay({
      title: "Termin anlegen",
      content: CreateAppointmentOverlay,
      bodyPadding: "none",
      closeOnParentOpen: true,
      isDark: true,
      properties: {
        init: buildFixedWorkOrderAppointmentInit({
          organizationId,
          employees,
          workOrder,
          properties,
          customers,
          contactPoints,
          scheduleEvents: allScheduleEvents?.length ? allScheduleEvents : scheduleEvents,
          workingHoursByUser,
          defaultStart: null,
          defaultEnd: null,
          defaultFlexType: "HARD",
          defaultStatus: "RESERVED",
          flexSlotMinutes: null,
          flexSlotsPerDay: null,
          allEmployeeIds,
        }),
        onCreated: async (event: ScheduleEvent) => {
          await handleScheduleChange(event)
          await reloadAfterContactOrderCreated()
        },
      },
    })
  }
</script>

{#if announcementSections.length}
  <div class="announcement-sections" data-testid="controlAdmin.workOrderDetail.announcements">
    {#each announcementSections as section (section.id)}
      {@const sectionInactive = announcementSectionIsInactive(section)}
      <section class="announcement-section">
        <SectionTitle
          small={true}
          brightBackground={true}
          title={`Terminnummer ${section.displayNumber}: ${section.title}`}
          subtitle={section.subtitle}
          helpModalTextId="workOrderOutreach"
          styleOuter="margin-bottom: 0.6rem;"
        >
          {#snippet buttonSnippet()}
            {#if section.event}
              <CTA
                type={hasOpenContactOrder(section) ? "primaryButton" : "secondaryButton"}
                darkMode={false}
                content="Termindetails"
                small={true}
                onClick={() => {
                  if (section.event) openEventDetails(section.event)
                }}
              >
                {#snippet iconSnippet()}
                  <Icon path={mdiCalendarClock} />
                {/snippet}
              </CTA>
            {/if}
          {/snippet}
        </SectionTitle>

        {#if section.event}
          <div class="announcement-plans">
            <div
              class="announcement-plan"
              class:announcement-plan-inactive={sectionInactive}
              aria-disabled={sectionInactive}
            >
              <CommunicationPlanTable
                {organizationId}
                {caseId}
                scheduleEventId={section.event.id}
                emptyText={emptyTextForSection(section)}
                {onPlanningContextInvalidated}
              />
            </div>
          </div>
        {:else if section.orders.length}
          <div class="announcement-plans">
            {#each section.orders as order (order.id)}
              {@const planInactive = announcementPlanIsInactive(order, section)}
              <div
                class="announcement-plan"
                class:announcement-plan-inactive={planInactive}
                aria-disabled={planInactive}
              >
                <CommunicationPlanTable
                  {organizationId}
                  {caseId}
                  contactOrderId={order.id}
                  emptyText={planInactive
                    ? CANCELED_ANNOUNCEMENT_PLAN_EMPTY_TEXT
                    : ANNOUNCEMENT_PLAN_EMPTY_TEXT}
                  {onPlanningContextInvalidated}
                />
              </div>
            {/each}
          </div>
        {/if}
      </section>
    {/each}
  </div>
{:else}
  <section class="announcement-section">
    <SectionTitle
      small={true}
      brightBackground={true}
      title="Kein aktiver Termin"
      description="Hier sehen Sie Termine und den Plan, wie Kunden darüber informiert werden. Für diesen Auftrag ist noch kein Termin geplant."
      helpModalTextId="workOrderOutreach"
      styleOuter="margin-bottom: 0.6rem;"
    >
      {#snippet buttonSnippet()}
        {#if workOrder}
          <CTA
            type="secondaryButton"
            darkMode={false}
            small={true}
            content="Termin anlegen"
            disabled={orderLocked}
            onClick={openCreateAppointment}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiCalendarClock} />
            {/snippet}
          </CTA>
        {/if}
      {/snippet}
    </SectionTitle>
    <div class="announcement-plans">
      <CommunicationPlanTable
        {organizationId}
        {caseId}
        emptyText={ANNOUNCEMENT_PLAN_EMPTY_TEXT}
        {onPlanningContextInvalidated}
      />
    </div>
  </section>
{/if}

<style lang="less">
  .announcement-sections,
  .announcement-plans {
    display: flex;
    flex-direction: column;
    gap: 1.8rem;
  }

  .announcement-section,
  .announcement-plan {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .announcement-plan {
    position: relative;
  }

  .announcement-plan-inactive {
    opacity: 0.5;
    user-select: none;
  }
</style>
